/**
 * Surprise me (D-158): a fight worth watching, not two names from a hat.
 *
 * Two uniform draws from the roster hand a kid a foregone conclusion five
 * times in six — the heavier animal wins 86% of all pairs — and a reveal that
 * says "heavier, the end". So the dice are loaded. Most rolls come from the
 * surprising pairs the build precomputes into roster.json (`pairs.surprising`:
 * the venom rule decided it, the winner is ten times lighter, or the scores are
 * inside the close band — D-136's own definition, the one the collection counts
 * by); a share are two animals from one category, a fair fight between things
 * of a kind; and the rest is anything at all, so a blue whale can still meet a
 * ladybug and be seven billion times heavier.
 *
 * Nothing here knows who wins. The list says a pair is worth watching, never
 * how it ends (invariant 1), and the client still has no combat scores.
 */

/** The shares. The remainder — 15% — is any two animals on the roster. */
export const SURPRISE_MIX = { surprising: 0.6, sameCategory: 0.25 }

let last = null

/**
 * @param {object} roster  public/roster.json as loaded
 * @param {() => number} [rnd]  0..1, for a seeded check
 * @returns {[string, string]} two slugs, never the same pair as the last call
 */
export function pickSurprise(roster, rnd = Math.random) {
  let pair = draw(roster, rnd)
  // The same pair twice running reads as the button being stuck; one more draw
  // is all it takes, and a roster of two is the only one that can repeat anyway.
  for (let tries = 0; tries < 4 && pairKey(pair) === last; tries++) pair = draw(roster, rnd)
  last = pairKey(pair)
  // Either side can be side a: the list stores i < j, and a kid should see the
  // eagle on the left as often as the right.
  return rnd() < 0.5 ? [pair[1], pair[0]] : pair
}

const pairKey = (p) => [...p].sort().join('__')
const pairsIn = (g) => (g.slugs.length * (g.slugs.length - 1)) / 2

function draw(roster, rnd) {
  const animals = roster.animals || []
  const surprising = roster.pairs?.surprising || []
  const r = rnd()
  if (r < SURPRISE_MIX.surprising && surprising.length) {
    const [i, j] = surprising[Math.floor(rnd() * surprising.length)]
    if (animals[i] && animals[j] && i !== j) return [animals[i].slug, animals[j].slug]
  }
  if (r < SURPRISE_MIX.surprising + SURPRISE_MIX.sameCategory) {
    // A category weighted by how many pairs it holds, so the two-animal
    // Legends tab is not a quarter of every same-category draw.
    const groups = (roster.groups || []).filter((g) => g.slugs.length >= 2)
    let pick = rnd() * groups.reduce((s, g) => s + pairsIn(g), 0)
    for (const g of groups) {
      pick -= pairsIn(g)
      if (pick < 0) return twoFrom(g.slugs, rnd)
    }
  }
  return twoFrom(animals.map((a) => a.slug), rnd)
}

/** Two different entries of a list of at least two. */
function twoFrom(slugs, rnd) {
  const i = Math.floor(rnd() * slugs.length)
  let j = Math.floor(rnd() * (slugs.length - 1))
  if (j >= i) j++
  return [slugs[i], slugs[j]]
}
